import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  IconButton,
  Chip,
  Stack,
  Divider,
  Paper,
  Avatar,
  Tooltip,
  LinearProgress,
  Badge,
  Button,
  Slide,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  QrCode as QrCodeIcon,
  Engineering as EngineeringIcon,
  Build as BuildIcon,
  Palette as CoatingIcon,
  CheckCircle as CompletedIcon,
  Schedule as InProgressIcon,
  AccessTime as PendingIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
  LocalShipping as DispatchIcon,
  GridOn as GridIcon,
  Window as WindowIcon,
  OpenInNew as OpenableIcon,
} from '@mui/icons-material';
import { formatDate, formatCurrency, formatStatusText } from '../../utils/formatters';
import OrderTimeline from './OrderTimeline';
import { getProductDisplayName } from '../../utils/productUtils';
import { Order, Product, Item, WorkflowLog } from '../../feature/orders/order';
import ItemQRCodeGenerator from '../orders/ItemQRCodeGenerator';

interface HierarchicalTimelineViewProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
  sidePanelMode?: boolean; // true for side panel, false for full screen with shifting panels
}

type ViewState = 'products' | 'items' | 'timeline';

const HierarchicalTimelineView: React.FC<HierarchicalTimelineViewProps> = ({
  isOpen,
  onClose,
  order,
  sidePanelMode = true, // Default to side panel mode
}) => {
  const theme = useTheme();
  const [currentView, setCurrentView] = useState<ViewState>('products');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  // Fixed 20% width per panel in cascading mode
  const widthPercent = 20;
  
  // QR Code modal state
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [selectedItemForQR, setSelectedItemForQR] = useState<Item | null>(null);

  // Get all items from all products
  const allItems = useMemo(() => {
    const items: Item[] = [];
    order.products?.forEach(product => {
      if (product.frame) items.push(...product.frame);
      if (product.panel) items.push(...product.panel);
      if (product.panelWindow) items.push(...product.panelWindow);
      if (product.net) items.push(...product.net);
      if (product.openableType) items.push(...product.openableType);
      if (product.items) items.push(...product.items);
    });
    return items;
  }, [order.products]);

  // Get items for selected product
  const productItems = useMemo(() => {
    if (!selectedProduct) return [];
    const items: Item[] = [];
    if (selectedProduct.frame) items.push(...selectedProduct.frame);
    if (selectedProduct.panel) items.push(...selectedProduct.panel);
    if (selectedProduct.panelWindow) items.push(...selectedProduct.panelWindow);
    if (selectedProduct.net) items.push(...selectedProduct.net);
    if (selectedProduct.openableType) items.push(...selectedProduct.openableType);
    if (selectedProduct.items) items.push(...selectedProduct.items);
    return items;
  }, [selectedProduct]);

  // Get item count for a specific product
  const getProductItemCount = (product: Product) => {
    let count = 0;
    if (product.frame) count += product.frame.length;
    if (product.panel) count += product.panel.length;
    if (product.panelWindow) count += product.panelWindow.length;
    if (product.net) count += product.net.length;
    if (product.openableType) count += product.openableType.length;
    if (product.items) count += product.items.length;
    return count;
  };

  // Convert item logs to timeline events
  const getTimelineEvents = (item: Item) => {
    if (!item.logs || item.logs.length === 0) {
      // If no logs, create a basic timeline based on current stage
      const stages = getStagesForItemType(item.type);
      return stages.map((stage, index) => {
        let status: 'completed' | 'in_progress' | 'pending' | 'skipped' = 'pending';
        
        // Find the current stage index in the stages array
        const currentStageIndex = stages.indexOf(item.currentStage || '');
        
        // Determine status based on sequential progression
        if (index < currentStageIndex) {
          // All stages before the current stage are completed
          status = 'completed';
        } else if (index === currentStageIndex) {
          // Current stage takes the item's status
          status = item.status as any || 'in_progress';
        } else {
          // All stages after the current stage are pending
          status = 'pending';
        }

        return {
          id: `stage-${index}`,
          stage: stage,
          status: status,
          user_id: item.assignedTo || 'system',
          user_name: item.assignedTo || 'System',
          timestamp: item.createdAt ? new Date(item.createdAt).toISOString() : new Date().toISOString(),
          notes: item.notes || '',
          duration_hours: null,
          qr_code: item.qrCode,
          stage_order: index,
          previous_stage: index > 0 ? stages[index - 1] : undefined,
          next_stage: index < stages.length - 1 ? stages[index + 1] : undefined,
        };
      });
    }
    
    return item.logs.map((log, index) => ({
      id: log.id,
      stage: log.stage,
      status: log.status as 'completed' | 'in_progress' | 'pending' | 'skipped',
      user_id: log.userId,
      user_name: log.userName,
      timestamp: log.timestamp.toString(),
      notes: log.notes,
      duration_hours: null,
      qr_code: item.qrCode,
      stage_order: index,
      previous_stage: log.previousStage,
      next_stage: undefined,
    }));
  };

  // Get stages for item type
  const getStagesForItemType = (itemType: string) => {
    const stageMap: Record<string, string[]> = {
      'Frame': ['Fabrication', 'Coating', 'Dispatch'],
      'Panel': [
        'Cutting', 'Assembly', 'Hinges', 'Member', 'Bar', 'Full Welding',
        'Grinding', 'Leveling', 'Finishing', 'Khacha', 'Coating',
        'Accessories', 'Glass Fitting', 'Dispatch'
      ],
      'Panel Window': ['Fabrication', 'Coating', 'Fitting in Panel'],
      'Net Fixing': ['Frame Cut', 'Weld', 'Net Fixing'],
      'Openable Type': ['Design', 'Fabrication', 'Assembly', 'Coating', 'Dispatch'],
    };
    return stageMap[itemType] || ['Fabrication', 'Assembly', 'Dispatch'];
  };

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    setCurrentView('items');
  };

  const handleItemSelect = (item: Item) => {
    setSelectedItem(item);
    setCurrentView('timeline');
  };

  const handleBack = () => {
    if (currentView === 'items') {
      setCurrentView('products');
      setSelectedProduct(null);
    } else if (currentView === 'timeline') {
      setCurrentView('items');
      setSelectedItem(null);
    }
  };

  const handleViewQR = (item: Item) => {
    console.log('Opening QR code for item:', item);
    setSelectedItemForQR(item);
    setShowQRCodeModal(true);
  };

  const handleCloseQRModal = () => {
    setShowQRCodeModal(false);
    setSelectedItemForQR(null);
  };

  const getItemTypeIcon = (type: string) => {
    const typeLower = type.toLowerCase();
    if (typeLower.includes('frame')) {
      return <EngineeringIcon />;
    } else if (typeLower.includes('panel') && typeLower.includes('window')) {
      return <WindowIcon />;
    } else if (typeLower.includes('panel')) {
      return <GridIcon />;
    } else if (typeLower.includes('net')) {
      return <GridIcon />;
    } else if (typeLower.includes('openable')) {
      return <OpenableIcon />;
    } else if (typeLower.includes('door')) {
      return <BuildIcon />;
    } else if (typeLower.includes('window')) {
      return <WindowIcon />;
    }
    return <BuildIcon />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'dispatched':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon />;
      case 'in_progress':
        return <InProgressIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'dispatched':
        return <DispatchIcon />;
      default:
        return <PendingIcon />;
    }
  };

  // Calculate panel position for cascading effect
  const getPanelPosition = (panelType: ViewState) => {
    if (sidePanelMode) return 0; // Not used in side panel mode
    
    // In full screen mode, calculate cascading positions
    if (panelType === 'products') {
      if (currentView === 'timeline') return 2; // 60% from right
      if (currentView === 'items') return 1; // 30% from right
      return 0; // Right edge
    } else if (panelType === 'items') {
      if (currentView === 'timeline') return 1; // 30% from right
      return 0; // Right edge
    } else if (panelType === 'timeline') {
      return 0; // Always right edge when visible
    }
    return 0;
  };

  const renderProductsView = () => (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'background.paper',
      borderLeft: sidePanelMode ? 'none' : 1,
      borderColor: 'divider',
      boxShadow: sidePanelMode ? 'none' : 2,
    }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" fontWeight={600}>
            Products ({order.products?.length || 0})
          </Typography>
          {sidePanelMode && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Select a product to view its items
        </Typography>
      </Box>

      {/* Products List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {!order.products || order.products.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No products found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This order doesn't have any products yet.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {order.products.map((product, index) => (
              <ListItem key={product.id} sx={{ p: 0, mb: 2 }}>
                <Card
                  sx={{
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4],
                    },
                  }}
                  onClick={() => handleProductSelect(product)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {index + 1}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {getProductDisplayName(product)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {product.type?.toUpperCase()} • {getProductItemCount(product)} items
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={formatStatusText(product.status || 'pending')}
                          color={getStatusColor(product.status || 'pending') as any}
                          size="small"
                          icon={getStatusIcon(product.status || 'pending')}
                        />
                        <ArrowForwardIcon color="action" />
                      </Box>
                    </Box>
                    
                    {/* Product Details */}
                    <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {product.label && (
                        <Chip label={`Label: ${product.label}`} size="small" variant="outlined" />
                      )}
                      {product.location && (
                        <Chip label={`Location: ${product.location}`} size="small" variant="outlined" />
                      )}
                      {product.evaOrder && (
                        <Chip label={`EVA: ${product.evaOrder}`} size="small" variant="outlined" />
                      )}
                    </Box>

                    {/* Progress Bar */}
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Progress
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {product.completed_items || 0} / {product.total_items || 0}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={product.total_items ? (product.completed_items || 0) / product.total_items * 100 : 0}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  const renderItemsView = () => (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'background.paper',
      borderLeft: sidePanelMode ? 'none' : 1,
      borderColor: 'divider',
      boxShadow: sidePanelMode ? 'none' : 2,
    }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {selectedProduct ? getProductDisplayName(selectedProduct) : 'Part(s)'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {productItems.length} Part(s) • Select an part to view timeline
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Items List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {productItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No items found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This set doesn't have any items yet.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {productItems.map((item, index) => (
              <ListItem key={item.id} sx={{ p: 0, mb: 2 }}>
                <Card
                  sx={{
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4],
                    },
                  }}
                  onClick={() => handleItemSelect(item)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          {getItemTypeIcon(item.type)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {item.itemNumber || `Item ${item.id.slice(-4)}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.type} • Stage: {item.currentStage || 'Not Started'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={formatStatusText(item.status)}
                          color={getStatusColor(item.status) as any}
                          size="small"
                          icon={getStatusIcon(item.status)}
                        />
                        <ArrowForwardIcon color="action" />
                      </Box>
                    </Box>
                    
                    {/* Item Details */}
                    <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {item.specifications?.dimensions && (
                        <Chip label={`Dimensions: ${item.specifications.dimensions}`} size="small" variant="outlined" />
                      )}
                      {item.specifications?.material && (
                        <Chip label={`Material: ${item.specifications.material}`} size="small" variant="outlined" />
                      )}
                      {item.assignedTo && (
                        <Chip label={`Assigned: ${item.assignedTo}`} size="small" variant="outlined" />
                      )}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {item.qrCode && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <QrCodeIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            QR: {item.qrCode}
                          </Typography>
                        </Box>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<QrCodeIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewQR(item);
                        }}
                        sx={{
                          minWidth: 'auto',
                          px: 1.5,
                          py: 0.5,
                          fontSize: '0.75rem',
                          textTransform: 'none'
                        }}
                      >
                        View QR
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  const renderTimelineView = () => (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'background.paper',
      borderLeft: sidePanelMode ? 'none' : 1,
      borderColor: 'divider',
      boxShadow: sidePanelMode ? 'none' : 2,
    }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {selectedItem?.itemNumber || `Item ${selectedItem?.id.slice(-4)}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedItem?.type} • {selectedItem?.currentStage || 'Not Started'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Timeline */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {selectedItem && (
          <OrderTimeline
            orderId={order.id}
            orderNumber={order.orderNumber || order.orderNumberNew || 'Unknown'}
            itemId={selectedItem.id}
            itemType={selectedItem.type}
            events={getTimelineEvents(selectedItem)}
            onClose={onClose}
          />
        )}
      </Box>
    </Box>
  );

  if (!isOpen) return null;

  // Side Panel Mode
  if (sidePanelMode) {
    return (
      <>
        {/* Backdrop */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: alpha(theme.palette.common.black, 0.5),
            zIndex: 1299,
          }}
          onClick={onClose}
        />
        
        {/* Side Panel */}
        <Slide direction="left" in={isOpen} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: { xs: '100vw', sm: '20vw' },
              height: '100vh',
              zIndex: 1300,
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: theme.shadows[8],
              borderLeft: 1,
              borderColor: 'divider',
            }}
          >
            {currentView === 'products' && renderProductsView()}
            {currentView === 'items' && renderItemsView()}
            {currentView === 'timeline' && renderTimelineView()}
          </Box>
        </Slide>
      </>
    );
  }

  // Full Screen Mode with Cascading Panels
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1300,
        bgcolor: 'transparent', // Make background transparent
        overflow: 'hidden',
        pointerEvents: 'none', // Allow clicks to pass through to background
      }}
    >
      {/* Close Button for Full Screen Mode */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1400,
          bgcolor: 'background.paper',
          boxShadow: 2,
          pointerEvents: 'auto', // Enable clicks on close button
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <CloseIcon />
      </IconButton>
      
      {/* Products Panel - Always visible, shifts left as other panels appear */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: `${getPanelPosition('products') * widthPercent}%`,
          width: `${widthPercent}%`,
          height: '100vh',
          zIndex: 1000 - getPanelPosition('products'),
          transition: 'right 0.3s ease-in-out',
          pointerEvents: 'auto', // Enable interactions with products panel
        }}
      >
        {renderProductsView()}
      </Box>

      {/* Items Panel - Only visible when in items or timeline view */}
      {(currentView === 'items' || currentView === 'timeline') && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: `${getPanelPosition('items') * widthPercent}%`,
            width: `${widthPercent}%`,
            height: '100vh',
            zIndex: 1000 - getPanelPosition('items'),
            transition: 'right 0.3s ease-in-out',
            pointerEvents: 'auto', // Enable interactions with items panel
          }}
        >
          {renderItemsView()}
        </Box>
      )}

      {/* Timeline Panel - Only visible when in timeline view */}
      {currentView === 'timeline' && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: `${getPanelPosition('timeline') * widthPercent}%`,
            width: `${widthPercent}%`,
            height: '100vh',
            zIndex: 1000 - getPanelPosition('timeline'),
            transition: 'right 0.3s ease-in-out',
            pointerEvents: 'auto', // Enable interactions with timeline panel
          }}
        >
          {renderTimelineView()}
        </Box>
      )}

      {/* Item QR Code Modal */}
      {showQRCodeModal && selectedItemForQR && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 1500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onClick={handleCloseQRModal}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 3,
              maxWidth: 500,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Item QR Code
              </Typography>
              <IconButton onClick={handleCloseQRModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            
            <ItemQRCodeGenerator
              itemId={selectedItemForQR.id}
              orderId={order.id}
              productId={selectedProduct?.id || ''}
              itemType={selectedItemForQR.type || 'item'}
              itemName={selectedItemForQR.itemNumber || selectedItemForQR.id}
              showQRCode={true}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default HierarchicalTimelineView;